/**
 * Centralized theme configuration for Kolega Platform
 * All colors, spacing, and design tokens should be defined here
 */

export const theme = {
  colors: {
    // Brand colors with mode variants
    brand: {
      primary: '#ADDD30',         // Lime Green - Primary brand color
      primaryDark: '#9CC429',     // Dark Lime - Hover states and secondary emphasis
      primaryDarkMode: '#C4F03E', // Bright Lime - For dark mode visibility
      primaryLight: '#C4F03E',    // Bright Lime - CTA elements
      
      // Mode-aware brand colors (for buttons, headers, etc.)
      action: {
        light: '#ADDD30',         // Lime Green in light mode
        dark: '#ADDD30',          // Lime Green in dark mode (keeping consistent)
      },
      actionHover: {
        light: '#9CC429',         // Dark Lime hover in light mode
        dark: '#9CC429',          // Dark Lime hover in dark mode (keeping consistent)
      },
      actionSecondary: {
        light: '#9CC429',         // Dark Lime secondary in light mode
        dark: '#9CC429',          // Dark Lime secondary in dark mode (keeping consistent)
      },
    },
    
    // Accent colors
    accent: {
      purple: '#7E22CE',          // Purple - Special elements and highlights
      purpleLight: '#A076F9',     // Light Purple - Secondary accent
      lavender: '#9063CD',        // Lavender - Subtle backgrounds and hover
      brightLime: '#C4F03E',      // Bright Lime - CTAs
    },
    
    // Semantic colors for different states
    semantic: {
      success: '#ADDD30',         // Using lime green for success
      info: '#A076F9',            // Using light purple for info
      warning: '#FFC107',         // Keeping amber for warning
      error: '#F44336',           // Keeping red for error
      blocked: '#F44336',
      pending: '#FFC107',
      inProgress: '#A076F9',
      completed: '#ADDD30',
    },
    
    // Agent-specific colors
    agents: {
      coder: {
        light: '#F5F0FF',         // Light purple tint
        dark: '#1D1D1F',          // Dark Gray instead of blue
        accent: '#A076F9',        // Light Purple
      },
      taskPlanning: {
        light: '#F0FCE8',         // Light lime tint
        dark: '#1D1D1F',          // Dark Gray instead of blue
        accent: '#ADDD30',        // Lime Green
      },
      investigation: {
        light: '#F5F0FF',         // Light purple tint
        dark: '#1D1D1F',          // Dark Gray instead of blue
        accent: '#7E22CE',        // Purple
      },
      browser: {
        light: '#F0F5FF',         // Light blue tint
        dark: '#1D1D1F',          // Dark Gray instead of blue
        accent: '#A076F9',        // Light Purple
      },
      system: {
        light: '#F5F5F7',         // Light Gray
        dark: '#1D1D1F',          // Dark Gray instead of blue
        accent: '#6E6E73',        // Medium Gray
      },
      default: {
        light: '#FFF9F0',         // Light orange tint
        dark: '#1D1D1F',          // Dark Gray instead of blue
        accent: '#F97316',        // Orange
      },
    },
    
    // UI element colors
    ui: {
      // Backgrounds
      background: {
        primary: {
          light: '#FFFFFF',       // White - Primary background
          dark: '#000000',        // Pure black - Primary dark background
        },
        secondary: {
          light: '#F8FAFC',       // Blue-tinted light gray - Secondary background
          dark: '#1A1A1A',        // Dark neutral gray - Secondary dark background
        },
        tertiary: {
          light: '#F1F5F9',       // Slightly bluer light gray - Alternate sections
          dark: '#2A2A2A',        // Medium neutral gray - Tertiary dark background
        },
        elevated: {
          light: '#FFFFFF',
          dark: '#1A1A1A',        // Dark neutral gray - Elevated dark background
        },
      },
      
      // Text colors
      text: {
        primary: {
          light: '#0F172A',       // Blue-black - Primary text
          dark: '#FFFFFF',        // Pure white - Primary text in dark mode
        },
        secondary: {
          light: '#475569',       // Blue-medium gray - Secondary text
          dark: '#B3B3B3',        // Light neutral gray - Secondary text in dark mode
        },
        tertiary: {
          light: '#64748B',       // Blue-gray - Tertiary text
          dark: '#808080',        // Medium neutral gray - Tertiary text in dark mode
        },
        muted: {
          light: '#94A3B8',       // Blue-lighter gray - Muted text
          dark: '#666666',        // Dark neutral gray - Muted text in dark mode
        },
      },
      
      // Border colors
      border: {
        primary: {
          light: '#CBD5E1',       // Blue-light border gray
          dark: '#404040',        // Medium neutral gray for dark mode borders
        },
        secondary: {
          light: '#E2E8F0',       // Blue-separator gray
          dark: '#2A2A2A',        // Dark neutral gray for dark mode borders
        },
        focus: {
          light: '#ADDD30',       // Lime Green for focus states
          dark: '#C4F03E',        // Bright Lime for dark mode focus
        },
      },
      
      // Interactive elements
      interactive: {
        hover: {
          light: '#F1F5F9',       // Slightly bluer light gray
          dark: '#2A2A2A',        // Medium neutral gray for dark mode hover
        },
        active: {
          light: '#E2E8F0',       // Blue-separator gray
          dark: '#404040',        // Medium neutral gray for dark mode active
        },
        disabled: {
          light: '#F8FAFC',       // Blue-tinted light gray
          dark: '#1A1A1A',        // Dark neutral gray for dark mode disabled
        },
      },
    },
    
    // Code editor specific colors
    editor: {
      background: {
        light: '#FFFFFF',
        dark: '#0F1419',          // Blue-black for code editor dark mode
      },
      border: {
        light: '#CBD5E1',         // Blue-light border gray
        dark: '#475569',          // Blue-medium gray for dark mode editor border
      },
    },
    
    // Gray scale for consistency (updated with bluer palette)
    gray: {
      50: '#F8FAFC',              // Blue-tinted very light gray
      100: '#F1F5F9',             // Blue-tinted light gray
      200: '#E2E8F0',             // Blue-separator gray
      300: '#CBD5E1',             // Blue-light border gray
      400: '#94A3B8',             // Blue-lighter gray
      500: '#64748B',             // Blue-gray
      600: '#475569',             // Blue-medium gray
      700: '#334155',             // Blue-dark gray
      800: '#1E293B',             // Blue-darker gray
      900: '#0F172A',             // Blue-black
    },
  },
  
  // Animation durations
  transitions: {
    fast: '150ms',
    normal: '300ms',
    slow: '500ms',
  },
  
  // Border radius values
  borderRadius: {
    sm: '0.25rem',
    md: '0.375rem',
    lg: '0.5rem',
    xl: '0.75rem',
    full: '9999px',
  },
  
  // Shadow definitions
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
  },
};

// Helper function to get color based on current theme mode
export const getThemeColor = (colorPath, isDarkMode) => {
  const keys = colorPath.split('.');
  let value = theme.colors;
  
  for (const key of keys) {
    value = value[key];
    if (!value) return undefined;
  }
  
  // If the value has light/dark variants
  if (value && typeof value === 'object' && ('light' in value || 'dark' in value)) {
    return isDarkMode ? value.dark : value.light;
  }
  
  return value;
};

// CSS custom properties generator
export const generateCSSVariables = (isDarkMode) => {
  const cssVars = {};
  
  // Brand colors
  cssVars['--color-brand-primary'] = theme.colors.brand.primary;
  cssVars['--color-brand-primary-dark'] = theme.colors.brand.primaryDark;
  cssVars['--color-brand-primary-dark-mode'] = theme.colors.brand.primaryDarkMode;
  cssVars['--color-brand-primary-light'] = theme.colors.brand.primaryLight;
  
  // Mode-aware brand colors
  cssVars['--color-brand-action'] = getThemeColor('brand.action', isDarkMode);
  cssVars['--color-brand-action-hover'] = getThemeColor('brand.actionHover', isDarkMode);
  cssVars['--color-brand-action-secondary'] = getThemeColor('brand.actionSecondary', isDarkMode);
  
  // Text color for brand-action buttons (black in both modes)
  cssVars['--color-brand-action-text'] = '#000000';
  
  // Accent colors
  cssVars['--color-accent-purple'] = theme.colors.accent.purple;
  cssVars['--color-accent-purple-light'] = theme.colors.accent.purpleLight;
  cssVars['--color-accent-lavender'] = theme.colors.accent.lavender;
  cssVars['--color-accent-bright-lime'] = theme.colors.accent.brightLime;
  
  // Design-system bridge: the legacy `ui-*` Tailwind tokens resolve to the
  // design-system tokens (src/design-system/tokens.css), which are themselves
  // theme-aware via :root / .dark — so these are intentionally theme-independent
  // strings here (the design token they point to flips with the theme).
  cssVars['--color-background-primary'] = 'var(--bg-base)';
  cssVars['--color-background-secondary'] = 'var(--surface-1)';
  cssVars['--color-background-tertiary'] = 'var(--surface-2)';

  cssVars['--color-text-primary'] = 'var(--text-primary)';
  cssVars['--color-text-secondary'] = 'var(--text-secondary)';
  cssVars['--color-text-tertiary'] = 'var(--text-muted)';

  cssVars['--color-border-primary'] = 'var(--border)';
  cssVars['--color-border-secondary'] = 'var(--border-subtle)';

  return cssVars;
}; 